import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { usePerson } from '../contexts/PersonContext'
import { useRoom, useRoomPresence } from '../hooks/useRoom'
import { useRoomChat } from '../hooks/useRoomChat'
import { RoomInfoCard } from '../components/rooms/RoomInfoCard'
import { PresenceCard } from '../components/rooms/PresenceCard'
import { LobbyChat } from '../components/rooms/LobbyChat'
import { Button } from '../components/ui/Button'
import { Modal } from '../components/ui/Modal'
import { closeRoom } from '../lib/api/rooms'
import { apiError } from '../lib/api/client'
import { useActionBoundary } from '../hooks/useActionBoundary'

/** Both roles can read visible rooms; host controls come from server capabilities. */
export function RoomPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { activePerson, flags } = usePerson()
  const qc = useQueryClient()
  const capture = useActionBoundary(id)
  const [confirmClose, setConfirmClose] = useState(false)
  const roomQuery = useRoom(id, activePerson?.id)
  const room = roomQuery.data
  const ready = !!room && !roomQuery.isError && room.info.isOpen !== false
  const chat = useRoomChat(id, room?.chat, ready, activePerson?.id)
  useRoomPresence(id, activePerson?.id, ready && !chat.unavailable)
  const close = useMutation({
    mutationFn: () => closeRoom(id as string),
    onMutate: () => ({ current: capture() }),
    onSuccess: async (_data, _variables, context) => {
      if (!context?.current()) return
      await qc.invalidateQueries({ queryKey: ['community', 'rooms', 'open'] })
      if (!context.current()) return
      qc.removeQueries({ queryKey: ['community', 'rooms', 'detail', id] })
      navigate('/')
    },
  })

  if (roomQuery.isLoading) return <p role="status" className="px-5 py-10">Loading the lobby…</p>
  if (roomQuery.isError || !room) {
    const status = apiError(roomQuery.error).status
    const denied = !!status && [403, 404, 410].includes(status)
    return <div role="alert" className="space-y-4 px-5 py-10">
      <p>{denied ? 'This room is closed or no longer available.' : 'The room could not be loaded. Check your connection and try again.'}</p>
      {!denied && <Button variant="outline" onClick={() => void roomQuery.refetch()}>Retry room</Button>}
      <Button onClick={() => navigate('/')}>Back to home</Button>
    </div>
  }

  const closed = room.info.isOpen === false || chat.unavailable
  return <div className="animate-rise px-5 py-6 sm:px-8">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <Button variant="outline" onClick={() => navigate('/')}>← Leave lobby</Button>
      <span className="font-mono text-[11px] uppercase tracking-label text-ink-muted">
        {closed ? 'Room closed or unavailable' : chat.connectionError ? 'Reconnecting to chat…' : `Live · ${room.presence.length} here now · chat filtered`}
      </span>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => navigate(room.info.circleId ? `/report?circleId=${encodeURIComponent(room.info.circleId)}` : '/report')}>Report someone</Button>
        {room.info.canClose && !closed && <Button variant="outline" onClick={() => setConfirmClose(true)}>Close room</Button>}
      </div>
    </div>
    {(chat.connectionError || room.info.isOpen === false) && <p role="alert" className="mt-4 rounded-btn border border-line p-4 text-sm">
      {chat.connectionError || 'This room is closed. You can read its chat; start or join another room to keep playing.'}
    </p>}
    <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <div className="min-w-0 space-y-6"><RoomInfoCard info={room.info} rules={room.rules} />{!closed && <PresenceCard presence={room.presence} />}</div>
      <LobbyChat key={`${id}:${activePerson?.id}`} messages={chat.messages} onSend={chat.send} sending={chat.sending} canSend={!flags.viewOnly && !closed} readOnlyReason={closed ? 'This room is no longer accepting messages.' : undefined} />
    </div>
    <Modal open={confirmClose} title="Close this room?" onClose={() => { if (!close.isPending) setConfirmClose(false) }}>
      <p>Everyone will leave this lobby. Its chat remains available to eligible circle members.</p>
      {close.isError && <p role="alert" className="mt-3">The room could not be closed. Try again.</p>}
      <div className="mt-5 flex flex-wrap justify-end gap-3">
        <Button variant="outline" disabled={close.isPending} onClick={() => setConfirmClose(false)}>Keep room open</Button>
        <Button disabled={close.isPending} onClick={() => close.mutate()}>{close.isPending ? 'Closing…' : 'Close for everyone'}</Button>
      </div>
    </Modal>
  </div>
}
